import { createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import moment from "moment-timezone";
import { Option } from "rc-select";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import Select from "../../../components/Inputs/Select";
import { dateTimeFormat } from "../../../utils/helpers/date";
import {
	getSettingsOrgResourceSearchFilters,
	getSettingsResourceSearchFilters,
	renderOrgLevelResourceLabel,
	renderSettingsResourceLabel,
} from "../../../utils/helpers/settings";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";
import { OrgLevelGroupsContext } from "../../Settings/General/OrganisationLevelGroups/context";
import StatusCell from "./StatusCell";

const columnHelper = createColumnHelper();

export const useColumns = ({ edit, remove, view, cancel, timeoff, namespace }) => {
	const { t } = useTranslation();
	const dateFormat = useCompanyDateFormat();
	const timeFormat = useCompanyTimeFormat();
	const { groups } = useContext(OrgLevelGroupsContext);

	const columns = useMemo(() => {
		const cols = [
			columnHelper.accessor("type", {
				enableColumnFilter: !!timeoff,
				header: !timeoff ? t("type") : t("plan"),
				cell: ({ row }) => {
					const type = row.original.type;
					const plan = row.original?.data?.plan?.plan;
					if (type === "absence" && plan) {
						return renderSettingsResourceLabel(plan);
					} else {
						return t(type);
					}
				},
				filterType: timeoff ? "resource-select" : undefined,
				Filter: timeoff && (
					<ResourceSelect
						mode="multiple"
						labelPropName="description"
						resourcePath="/timeoff"
						renderLabel={renderSettingsResourceLabel}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				),
			}),
		];

		if (namespace === "timesheet") {
			cols.splice(0, 0, columnHelper.accessor("date", {
				header: t("date"),
				cell: ({ row: { original } }) => {
					const date = original?.data?.date;
					return date ? moment(date).format(dateFormat) : null;
				}
			}),
			);
		}

		if (!["availability", "timesheet"].includes(namespace)) {
			cols.splice(
				0,
				0,
				columnHelper.accessor("startDate", {
					enableColumnFilter: true,
					header: t("start-date"),
					cell: (info) => {
						const { data } = info.row.original;
						return dateTimeFormat(data?.date || data?.startDate, dateFormat);
					},
				}),
				columnHelper.accessor("startTime", {
					header: t("start-time"),
					cell: (info) => {
						const { data } = info.row.original;
						return dateTimeFormat(data?.startDate, timeFormat);
					},
				}),
				columnHelper.accessor("endDate", {
					header: t("end-date"),
					cell: (info) => {
						const { data } = info.row.original;
						return dateTimeFormat(data?.date || data?.endDate, dateFormat);
					},
				}),
				columnHelper.accessor("endTime", {
					header: t("end-time"),
					cell: (info) => {
						const { data } = info.row.original;
						return dateTimeFormat(data?.endDate, timeFormat);
					},
				})
			);
		}

		if (!timeoff && namespace !== "timesheet") {
			cols.splice(5, 0,
				columnHelper.accessor("data.location", {
					enableColumnFilter: true,
					header: t("location"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/locations"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				})
			);
			cols.splice(6, 0,
				columnHelper.accessor("data.job", {
					enableColumnFilter: true,
					header: t("job"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/jobs"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				})
			);
		}

		if (groups) {
			const groupColumns = groups.map((group) => {
				const level = `${group.level}`.replace("_", "");
				return columnHelper.accessor(level, {
					enableColumnFilter: true,
					header: group.description,
					cell: (info) => {
						const value = info.getValue();
						if (value && group.allocatedBy) {
							return renderOrgLevelResourceLabel(value);
						}
						return renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath={`/${group?.id}/org-levels`}
							mode="multiple"
							renderLabel={renderOrgLevelResourceLabel}
							getSearchFilters={
								getSettingsOrgResourceSearchFilters
							}
							valuePropName="id"
							hasSearch
						/>
					),
				});
			});
			cols.splice(4, 0, ...groupColumns);
		}

		cols.push(
			columnHelper.accessor("status", {
				enableColumnFilter: true,
				header: t("status"),
				cell: ({ row }) => <StatusCell value={row.original} />,
				filterType: "select",
				Filter: (
					<Select>
						<Option value="approved">{t("approved")}</Option>
						<Option value="denied">{t("denied")}</Option>
						<Option value="pending">{t("pending")}</Option>
						<Option value="canceled">{t("canceled")}</Option>
						<Option value="expired">{t("expired")}</Option>
					</Select>
				),
			})
		);

		if (edit || remove || cancel || view) {
            const actionsColumn = columnHelper.display({
                id: "actions",
                enableHiding: false,
                enableColumnFilter: false,
                header: () => (
                    <div className="text-right">
                        {t("actions")}
                    </div>
                ),
				cell: ({ row: { original } }) => {
					const viewAction = typeof view === "function" ? view(original) : null;
					const editAction = typeof edit === "function" ? edit(original) : null;
					const removeAction = typeof remove === "function" ? remove(original) : null;
					const cancelAction = typeof cancel === "function" ? cancel(original) : null;

					if (
						original?.status === "canceled"
						|| (original?.status === "approved" && original?.type === "cancel")
					) {
						return null;
					}

					return (
						<div
							className={classNames(
								"d-flex justify-content-end",
							)}
						>
							{viewAction && (
                                <span
                                    className="fa-solid fa-eye mr-3 pb-2 text-primary cursor-pointer"
                                    onClick={viewAction}
                                    style={{ fontSize: "17px" }}
                                />
							)}

							{
								editAction
								&& original.status === "pending"
								&& !["submit-schedule", "cancel", "swap-shifts", "offer-shifts", "availability", "meal-waive"].includes(original?.type)
								&& (
									<span
										className="btn-inner--icon icon-sm cursor-pointer text-primary"
										onClick={editAction}
									>
										<i className="fas fa-pen" />
									</span>
								)}

							{removeAction && original.status === "pending" && original?.type !== "submit-schedule" && (
								<span
									className="btn-inner--icon mr-3 icon-sm cursor-pointer text-primary"
									onClick={removeAction}
								>
									<i className="fas fa-trash" />
								</span>
							)}

							{cancelAction && original.status === "approved" && (original?.type === "absence" || original?.type === "meal-waive") && (
								<span
									className="btn-inner--icon mr-3 icon-sm cursor-pointer text-primary"
									onClick={cancelAction}
									title={t("cancel-request")}
								>
									<i className="fas fa-ban" />
								</span>
							)}
						</div>
					);
				}
			});
			cols.push(actionsColumn);
		}

		return cols;
	}, [timeoff, t, namespace, edit, remove, cancel, groups, dateFormat, timeFormat, view]);

	return columns;
};
